import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import COPY from './copy.json';
import SERVICES from './services.json';
import {
  t, PHONE, PHONE_LABEL, initReveal, Figure,
  IconPhone, IconTick, IconArrow,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Satu komponen untuk empat halaman layanan: tyre, fuel, car services, car wash.
 * Yang membedakan cuma prop `service` — struktur, daftar harga dan FAQ semuanya
 * dibaca dari services.json + copy.json.
 *
 * ponytail: ServiceRecovery.js sengaja TIDAK ikut dilebur ke sini. Halaman itu
 * punya tiga bagian yang tidak dimiliki keempat halaman ini (kapan perlu
 * recovery, sambil menunggu, tabel harga 8 baris), dan strukturnya sudah
 * diverifikasi cocok dengan HTML paket client. Meleburnya berarti menambah
 * cabang kondisional demi satu pemakai. Kalau nanti recovery disederhanakan,
 * barulah gabungkan.
 *
 * Harga di services.json adalah SNAPSHOT DB, bukan data hidup — lihat _readme
 * di file itu.
 */

// Layanan lain yang ditawarkan di kaki tiap halaman.
const ALL = ['recovery', 'tyre', 'fuel', 'carservices', 'carwash'];
const PATHS = { recovery: '/service-recovery' };
ALL.slice(1).forEach(k => { PATHS[k] = SERVICES[k].path; });

// Harga termurah suatu layanan, untuk kartu "layanan lain".
const cheapest = key => {
  if (key === 'recovery') return null; // per-km, tidak punya harga tetap
  const prices = SERVICES[key].items.map(i => i.price).filter(p => p != null);
  return prices.length ? Math.min(...prices) : null;
};

const FAQ_KEYS = [1, 2, 3];

const ldService = (key, locale) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: SERVICES[key].categoryName,
  serviceType: SERVICES[key].categoryName,
  provider: {
    '@type': 'AutoRepair', name: 'Bas Recovery',
    telephone: PHONE, url: 'https://basrecovery.com/'
  },
  areaServed: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']
    .map(name => ({ '@type': 'City', name })),
  // Satu Offer per subkategori: itulah yang benar-benar bisa dipesan di aplikasi.
  offers: SERVICES[key].items.filter(i => i.price != null).map(i => ({
    '@type': 'Offer',
    name: i.name,
    price: String(i.price),
    priceCurrency: 'AED'
  }))
});

const ldFaq = key => ({
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ_KEYS
    .filter(n => COPY['en-US'][`${key}.faq.${n}.q`])
    .map(n => ({
      '@type': 'Question',
      name: COPY['en-US'][`${key}.faq.${n}.q`],
      acceptedAnswer: { '@type': 'Answer', text: COPY['en-US'][`${key}.faq.${n}.a`] }
    }))
});

const ldCrumb = (key, crumbName) => ({
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://basrecovery.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://basrecovery.com/service-recovery' },
    { '@type': 'ListItem', position: 3, name: crumbName }
  ]
});

class ServicePage extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  render() {
    const { navOpen } = this.state;
    const { service } = this.props;
    const spec = SERVICES[service];
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);
    const k = key => `${service}.${key}`;

    const from = cheapest(service);
    const others = ALL.filter(o => o !== service);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current={spec.path}
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          {/* ── HERO ── harga dan tombol telepon lebih dulu, bukan pitch. */}
          <section className="hero" style={{ paddingBlock: 'clamp(40px,6vw,72px) clamp(48px,7vw,80px)' }}>
            <div className="wrap">
              <nav aria-label="Breadcrumb" style={{ marginBottom: 'var(--sp-5)' }}>
                <ol style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '9px', flexWrap: 'wrap',
                  fontSize: 'var(--fs-xs)', color: 'var(--on-night-2)' }}>
                  <li><a href="/" style={{ color: 'var(--on-night-2)' }}>{tx('nav.home')}</a></li>
                  <li aria-hidden="true">/</li>
                  <li><a href="/service-recovery" style={{ color: 'var(--on-night-2)' }}>{tx('nav.services')}</a></li>
                  <li aria-hidden="true">/</li>
                  <li><span style={{ color: 'var(--orange-hot)' }}>{tx(k('crumb'))}</span></li>
                </ol>
              </nav>

              <div className="hero-grid">
                <div>
                  <p className="eyebrow">{tx(k('eyebrow'))}</p>
                  <h1 style={{ fontSize: 'var(--fs-h1)', maxWidth: '22ch' }}>{tx(k('h1'))}</h1>
                  <p className="lead">{tx(k('lead'))}</p>
                  <div className="hero-cta">
                    <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                      <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                    </a>
                    <a className="btn btn-ghost btn-lg" href="/contact">{tx('cta.callback')}</a>
                  </div>
                </div>

                <aside className="eta-card" aria-label={tx('srec.etaAria')}>
                  <div style={{ display: 'flex', gap: 'var(--sp-6)', flexWrap: 'wrap' }}>
                    <div>
                      <div className="eta-big" style={{ fontSize: 'clamp(40px,5vw,58px)' }}>
                        <span style={{ fontSize: '.45em' }}>AED </span>{from}
                      </div>
                      <p className="eta-label">{tx('svc.priceLabel')}</p>
                    </div>
                    <div>
                      <div className="eta-big" style={{ fontSize: 'clamp(40px,5vw,58px)' }}>
                        {tx(k('eta'))}<span> {tx('eta.unit')}</span>
                      </div>
                      <p className="eta-label">{tx('srec.etaLabel')}</p>
                    </div>
                  </div>
                  <div className="eta-rule" />
                  <div className="eta-rows">
                    {[1, 2, 3, 4].map(n => (
                      <div className="eta-row" key={n}>
                        <IconTick /><span><b>{tx(k(`spec.${n}b`))}</b>{tx(k(`spec.${n}`))}</span>
                      </div>
                    ))}
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {/* ── DAFTAR HARGA ──
              Satu baris per subkategori yang benar-benar bisa dipesan di
              aplikasi. Nama dan angka berasal dari DB, bukan dikarang. */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx(k('price.h2'))}</h2>
                <p className="lead">{tx(k('price.lead'))}</p>
              </div>
              <div className="cov-grid">
                {spec.items.map(item => (
                  <div className="cov" key={item.slug}>
                    <span>
                      {tx(k(`item.${item.slug}`))}
                      <small>
                        {item.price != null
                          ? `${tx('svc.from')} AED ${item.price} · ${tx(k(`itemNote.${item.slug}`))}`
                          : tx(k(`itemNote.${item.slug}`))}
                      </small>
                    </span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 'var(--sp-5)', color: 'var(--ink-2)', fontSize: 'var(--fs-sm)' }}>
                {tx('svc.price.foot')}
              </p>
            </div>
          </section>

          {/* ── APA YANG TERMASUK ── */}
          <section>
            <div className="wrap split">
              <div>
                <h2>{tx(k('incl.h2'))}</h2>
                <p className="lead">{tx(k('incl.lead'))}</p>
                <div className="ticks">
                  {[1, 2, 3, 4].map(n => (
                    <div className="tick" key={n}>
                      <IconTick /><span><strong>{tx(k(`incl.${n}b`))}</strong>{tx(k(`incl.${n}`))}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Figure photo={spec.photo} alt={tx(k('figureAlt'))} caption={tx(k('figure'))} />
            </div>
          </section>

          {/* ── LAYANAN LAIN ── */}
          <section className="band-sand">
            <div className="wrap">
              <div className="sec-head"><h2>{tx('svc.other.h2')}</h2></div>
              <div className="svc-grid">
                {others.map(o => {
                  const price = cheapest(o);
                  return (
                    <a className="svc" href={PATHS[o]} key={o}>
                      <h3>{tx(`svc.card.${o}.title`)}</h3>
                      <p>{tx(`svc.card.${o}.body`)}</p>
                      <span className="svc-more">
                        {price != null ? `${tx('svc.from')} AED ${price}` : tx('svc.byDistance')} <IconArrow />
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section>
            <div className="wrap-narrow">
              <div className="sec-head"><h2>{tx('svc.faq.h2')}</h2></div>
              <div className="faq">
                {FAQ_KEYS.map((n, i) => (
                  <details key={n} open={i === 0}>
                    <summary>{tx(k(`faq.${n}.q`))}</summary>
                    <div><p>{tx(k(`faq.${n}.a`))}</p></div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA ── */}
          <section className="band-night">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <h2 style={{ maxWidth: '26ch', marginInline: 'auto' }}>{tx(k('cta.h2'))}</h2>
              <p style={{ maxWidth: '50ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx(k('cta.lead'))}</p>
              <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
              </a>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldService(service, L)) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldFaq(service)) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldCrumb(service, COPY['en-US'][`${service}.crumb`])) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(ServicePage);
